import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Shield, X } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

/**
 * Atalho flutuante discreto para o painel administrativo.
 * Só renderiza para usuários com papel `admin`.
 * Oculto dentro das próprias rotas /admin para não poluir a UI.
 *
 * Atalhos de teclado (somente admin):
 *  - Ctrl/⌘ + Shift + A  → abre /admin
 *  - Shift + ?           → mostra dica dos atalhos
 */
const AdminShortcutFAB = () => {
  const { isAdmin, roleLoading } = useUserRole();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const modKeyLabel = isMac ? "⌘" : "Ctrl";
  const shortcutLabel = `${modKeyLabel} + Shift + A`;

  useEffect(() => {
    if (!isAdmin) return;

    const handler = (e: KeyboardEvent) => {
      // Ignora quando o foco está em campos editáveis.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const editable =
        target?.isContentEditable ||
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT";
      if (editable) return;

      const mod = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/⌘ + Shift + A → abrir painel admin
      if (mod && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        if (!pathname.startsWith("/admin")) navigate("/admin");
        return;
      }

      // Shift + ? → mostrar dica dos atalhos
      if (e.shiftKey && e.key === "?") {
        e.preventDefault();
        toast.message("Atalhos administrativos", {
          description: `${shortcutLabel} — abrir painel administrativo`,
        });
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isAdmin, isMac, navigate, pathname, shortcutLabel]);

  if (roleLoading || !isAdmin || dismissed) return null;
  if (pathname.startsWith("/admin")) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "fixed z-40 bottom-24 right-3 flex items-center gap-1",
          "sm:bottom-6 sm:right-6"
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => navigate("/admin")}
              aria-label={`Acesso administrativo (${shortcutLabel})`}
              aria-keyshortcuts={isMac ? "Meta+Shift+A" : "Control+Shift+A"}
              className={cn(
                "h-11 w-11 rounded-full shadow-lg border border-primary/40",
                "bg-background/80 backdrop-blur-md text-primary",
                "flex items-center justify-center",
                "hover:bg-primary hover:text-primary-foreground transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary/60"
              )}
            >
              <Shield className="h-5 w-5" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="flex flex-col gap-0.5">
            <span className="font-medium">Acesso administrativo</span>
            <span className="text-[10px] opacity-80">
              Atalho:{" "}
              <kbd className="rounded bg-muted px-1 py-0.5 text-[10px]">
                {shortcutLabel}
              </kbd>
            </span>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Ocultar atalho administrativo"
              className={cn(
                "h-6 w-6 rounded-full border border-border/60",
                "bg-background/70 backdrop-blur text-muted-foreground",
                "flex items-center justify-center hover:text-foreground"
              )}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">Ocultar atalho</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default AdminShortcutFAB;
